import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X, AlertCircle } from 'lucide-react';
import { API_BASE_URL } from '../services/api';
import InstallPWAButton from './InstallPWAButton';
const Header = ({ onLogout }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Get user data from localStorage
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  // Fetch notifications (same stage 15+ days)
  const fetchNotifications = async () => {
    setLoadingNotifications(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/project/report/same-stage?days=15`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (response.ok) {
        setNotifications(data.data || []);
        setNotificationCount(data.data?.length || 0);
      } else {
        console.error('Failed to fetch notifications:', data.message);
        setNotifications([]);
        setNotificationCount(0);
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setNotifications([]);
      setNotificationCount(0);
    } finally {
      setLoadingNotifications(false);
    }
  };

  // Fetch notifications on component mount - only for non-admin users
  useEffect(() => {
    if (user && user.role !== 'admin') {
      fetchNotifications();
      // Set up interval to refresh notifications every 5 minutes
      const interval = setInterval(fetchNotifications, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const handleNavigation = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const handleNotificationItemClick = (item) => {
    navigate(`/inspect?houseId=${item.h_id}`);
    setNotificationOpen(false);
    setMobileMenuOpen(false);
  };

  const handleNotificationClick = () => {
    setNotificationOpen(!notificationOpen);
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };

  const isAdmin = user?.role === 'admin';

  // Define menu items based on role
  const getMenuItems = () => {
    const baseItems = [
      {
        path: '/dashboard',
        icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
        label: 'Home',
        showFor: ['admin', 'overseer', 'inspector']
      },
      {
        path: '/inspect',
        icon: 'M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
        label: 'Inspect',
        mobileLabel: 'New Inspection',
        showFor: [ 'overseer','admin']
      },
      {
        path: '/list',
        icon: 'M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
        label: 'List',
        mobileLabel: 'Inspection List',
    showFor: ['admin', 'overseer']
      },
      {
        path: '/upload',
        icon: 'M15 17h5l-5 5-5-5h5v-13a1 1 0 011-1h4a1 1 0 011 1v13z',
        label: 'Housing Data',
        mobileLabel: 'Upload Data',
        showFor: ['admin','overseer']
      },
      {
        path: '/reports',
        icon: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
        label: 'Reports',
        showFor: ['admin', 'overseer', 'zonal', 'dist-bdo', 'dist-ddo', 'dist-ac', 'state']
      },
      {
        path: '/payment',
        icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2-3-.895-3-2m3-6v2m0 12v2m-7-7h14',
        label: 'Payment',
        showFor: ['admin', 'block-apo']
      },
      {
        path: '/master',
        icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10',
        label: 'Master',
        mobileLabel: 'Masters',
        showFor: ['admin']
      },
      {
        path: '/user-creation',
        icon: 'M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z',
        label: 'Users',
        mobileLabel: 'User Creation',
        showFor: ['admin']
      }
    ];

    return baseItems.filter(item => item.showFor.includes(user?.role));
  };

  const menuItems = getMenuItems();

  return (
    <header className="bg-sky-400 shadow-lg" style={{ backgroundColor: '#39b0d2' }}>
      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
        <div className="flex justify-between items-center py-2 sm:py-4">
          {/* Left Section - Logo & Title */}
          <div className="flex items-center space-x-2 sm:space-x-4 flex-1 min-w-0">
            <img 
              src="images/tnlogo.png" 
              alt="TN Government Logo" 
              className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 flex-shrink-0"
            />
            <div className="min-w-0 flex-1">
              <h1 className="text-sm sm:text-base md:text-lg lg:text-xl font-bold text-gray-900">
                Rural Housing Inspection Platform - Villupuram
              </h1>
              <p className="text-xs sm:text-sm text-gray-700">
                Rural Development and Panchayat Raj Department
              </p>
            </div>
          </div>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center space-x-4 xl:space-x-6 flex-shrink-0">
            {/* Menu Items */}
            <nav className="flex items-center space-x-2 xl:space-x-4">
              {menuItems.map((item) => (
                <button 
                  key={item.path}
                  onClick={() => handleNavigation(item.path)}
                  className="flex items-center space-x-1 px-2 xl:px-3 py-2 rounded-lg hover:bg-white/10 transition duration-200"
                >
                  <svg className="w-4 h-4 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                  </svg>
                  <span className="text-xs xl:text-sm font-medium text-gray-900 hidden xl:inline">{item.label}</span>
                </button>
              ))}
            </nav>

                <InstallPWAButton />

            {/* Notification Bell - Hide for admin users */}
            {!isAdmin && (
              <div className="relative">
                <button
                  onClick={handleNotificationClick}
                  className="flex items-center p-2 rounded-lg hover:bg-white/10 transition duration-200 relative"
                >
                  <Bell className="w-5 h-5 text-gray-900" />
                  {notificationCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {notificationCount > 99 ? '99+' : notificationCount}
                    </span>
                  )}
                </button>

                {/* Notification Dropdown */}
                {notificationOpen && (
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50 max-h-96 overflow-y-auto">
                    <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-gray-900">
                        Pending Inspections (15+ Days)
                      </h3>
                      <button
                        onClick={() => setNotificationOpen(false)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    
                    {loadingNotifications ? (
                      <div className="px-4 py-6 text-center">
                        <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-2"></div>
                        <p className="text-sm text-gray-600">Loading...</p>
                      </div>
                    ) : notifications.length > 0 ? (
                      <div className="max-h-72 overflow-y-auto">
                        {notifications.slice(0, 10).map((item, index) => (
                          <div key={index} 
                               onClick={() => handleNotificationItemClick(item)}
                               className="px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 cursor-pointer transition-colors duration-200">
                            <div className="flex items-start space-x-3">
                              <AlertCircle className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate hover:text-blue-600">
                                  {item.h_id}
                                </p>
                                <p className="text-xs text-gray-600 truncate">
                                  {item.beneficiary}
                                </p>
                                <div className="flex items-center justify-between mt-1">
                                  <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
                                    {item.stage}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {item.panchayat}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                        
                        {notifications.length > 10 && (
                          <div className="px-4 py-3 text-center border-t border-gray-200">
                            <button
                              onClick={() => {
                                handleNavigation('/reports');
                                setNotificationOpen(false);
                              }}
                              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                            >
                              View All {notifications.length} Items
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="px-4 py-6 text-center">
                        <Bell className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600">No pending inspections</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* User Profile Dropdown */}
            <div className="relative">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="flex items-center space-x-2 p-1 rounded-lg hover:bg-white/10 transition duration-200"
              >
                <div className="w-6 h-6 xl:w-8 xl:h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <svg className="w-3 h-3 xl:w-4 xl:h-4 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <svg className="w-3 h-3 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {mobileMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                  {user && (
                    <div className="px-4 py-3 border-b border-gray-200">
                      <p className="text-sm font-semibold text-gray-900 truncate">{user.name}</p>
                      <p className="text-xs text-gray-600 capitalize">{user.role}</p>
                    </div>
                  )}
                  <button
                    onClick={onLogout}
                    className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition duration-200"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center space-x-2 flex-shrink-0">
             <InstallPWAButton compact />
            {/* Mobile Notification Bell - Hide for admin users */}
            {!isAdmin && (
              <div className="relative">
                <button
                  onClick={handleNotificationClick}
                  className="flex items-center p-2 rounded-lg hover:bg-white/10 transition duration-200 relative"
                >
                  <Bell className="w-5 h-5 text-gray-900" />
                  {notificationCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                      {notificationCount > 9 ? '9+' : notificationCount}
                    </span>
                  )}
                </button>
              </div>
            )}

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg hover:bg-white/10 transition duration-200"
            >
              <svg className="w-6 h-6 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Notification Dropdown - Hide for admin users */}
        {!isAdmin && notificationOpen && (
          <div className="lg:hidden border-t border-white/20 py-4">
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 mx-2">
              <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">
                  Pending Inspections (15+ Days)
                </h3>
                <button
                  onClick={() => setNotificationOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              
              {loadingNotifications ? (
                <div className="px-4 py-6 text-center">
                  <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-2"></div>
                  <p className="text-sm text-gray-600">Loading...</p>
                </div>
              ) : notifications.length > 0 ? (
                <div className="max-h-60 overflow-y-auto">
                  {notifications.slice(0, 5).map((item, index) => (
                    <div key={index} 
                         onClick={() => handleNotificationItemClick(item)}
                         className="px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 cursor-pointer transition-colors duration-200">
                      <div className="flex items-start space-x-3">
                        <AlertCircle className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate hover:text-blue-600">
                            {item.h_id}
                          </p>
                          <p className="text-xs text-gray-600 truncate">
                            {item.beneficiary}
                          </p>
                          <div className="flex items-center justify-between mt-1">
                            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
                              {item.stage}
                            </span>
                            <span className="text-xs text-gray-500">
                              {item.panchayat}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {notifications.length > 5 && (
                    <div className="px-4 py-3 text-center border-t border-gray-200">
                      <button
                        onClick={() => {
                          handleNavigation('/reports');
                          setNotificationOpen(false);
                        }}
                        className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                      >
                        View All {notifications.length} Items
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="px-4 py-6 text-center">
                  <Bell className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">No pending inspections</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Mobile Menu */}
        {mobileMenuOpen && !notificationOpen && (
          <div className="lg:hidden border-t border-white/20 py-4">
            <div className="space-y-3">
              {/* User Info Mobile */}
              {user && (
                <div className="flex items-center space-x-3 px-2 py-2">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{user.name}</p>
                    <p className="text-xs text-gray-700 capitalize truncate">{user.role}</p>
                  </div>
                </div>
              )}

              {/* Menu Items Mobile */}
              <div className="space-y-1">
                {menuItems.map((item) => (
                  <button 
                    key={item.path}
                    onClick={() => handleNavigation(item.path)}
                    className="w-full flex items-center space-x-3 px-2 py-3 rounded-lg hover:bg-white/10 transition duration-200"
                  >
                    <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                    </svg>
                    <span className="text-sm font-medium text-gray-900">{item.mobileLabel || item.label}</span>
                  </button>
                ))}
                
                <button 
                  onClick={onLogout}
                  className="w-full flex items-center space-x-3 px-2 py-3 rounded-lg hover:bg-white/10 transition duration-200 border-t border-white/20 mt-3 pt-3"
                >
                  <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span className="text-sm font-medium text-gray-900">Logout</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;